import { assign, createMachine } from 'xstate'
import { ActiveKey, HitsByIndex } from '../types/Hits'

export interface AlgoliaSearchMachineContext {
  query: string
  inputFocused: boolean
  isModalOpen: boolean
  hits: any
  hasResults: boolean
  loading: boolean
  activeKey: string
  showSuggestions: boolean
}

type ToggleModalEvent = { type: 'TOGGLE_MODAL'; isModalOpen: boolean }
type SetQueryEvent = { type: 'SET_QUERY'; query: string }
type SetLoadingEvent = { type: 'SET_LOADING'; loading: boolean }
type SetActiveKeyEvent = { type: 'SET_ACTIVE_KEY'; activeKey: string }
type SetInputFocusEvent = { type: 'SET_INPUT_FOCUS'; inputFocused: boolean }
type SetShowSuggestionsEvent = { type: 'SET_SHOW_SUGGESTIONS'; showSuggestions: boolean }
type SetHitsEvent = { type: 'SET_HITS'; hits: HitsByIndex; hasResults: boolean; resultKeys: string[]; loading: boolean; activeKey: ActiveKey }

type AlgoliaSearchMachineEvent =
  | ToggleModalEvent
  | SetQueryEvent
  | SetLoadingEvent
  | SetActiveKeyEvent
  | SetInputFocusEvent
  | SetHitsEvent
  | SetShowSuggestionsEvent

type SearchState = 'empty' | 'searching' | 'resultsReturned' | 'noResults' | 'timedOut'
type State = {
  value: SearchState
  context: AlgoliaSearchMachineContext
}

const initialContext: AlgoliaSearchMachineContext = {
  query: '',
  inputFocused: false,
  isModalOpen: false,
  hits: [],
  hasResults: false,
  loading: false,
  activeKey: '',
  showSuggestions: false
}

export const algoliaSearchMachine = createMachine<AlgoliaSearchMachineContext, AlgoliaSearchMachineEvent, State>(
  {
    id: 'algoliaSearchMachine',
    context: { ...initialContext },
    initial: 'searching',
    on: {
      TOGGLE_MODAL: { actions: ['toggleModal'] },
      SET_QUERY: { actions: ['setQuery'] },
      SET_HITS: { target: 'searching', actions: ['setHits'] },
      SET_ACTIVE_KEY: { actions: ['setActiveKey'] },
      SET_INPUT_FOCUS: { actions: ['setInputFocus'] },
      SET_SHOW_SUGGESTIONS: { actions: ['setShowSuggestions'] },
      SET_LOADING: { actions: ['setLoading'] }
    },
    states: {
      empty: {},
      resultsReturned: {},
      noResults: {},
      searching: {
        always: [
          { target: 'empty', cond: ({ query }) => !query },
          { target: 'resultsReturned', cond: ({ query, hasResults }) => Boolean(query && hasResults) },
          { target: 'noResults', cond: ({ query, hasResults, loading }) => Boolean(query && !hasResults && !loading) }
        ]
      }
    }
  },

  {
    actions: {
      setInputFocus: assign<AlgoliaSearchMachineContext, SetInputFocusEvent>((_, { inputFocused }) => ({
        inputFocused
      })) as any,
      toggleModal: assign<AlgoliaSearchMachineContext, ToggleModalEvent>((_, { isModalOpen }) => ({
        isModalOpen
      })) as any,
      setQuery: assign<AlgoliaSearchMachineContext, SetQueryEvent>((_, { query }) => ({
        query
      })) as any,
      setLoading: assign<AlgoliaSearchMachineContext, SetLoadingEvent>((_, { loading }) => ({
        loading
      })) as any,
      setActiveKey: assign<AlgoliaSearchMachineContext, SetActiveKeyEvent>((_, { activeKey }) => ({
        activeKey
      })) as any,
      setHits: assign<AlgoliaSearchMachineContext, SetHitsEvent>((_, ctx) => ({
        ...ctx
      })) as any,
      setShowSuggestions: assign<AlgoliaSearchMachineContext, SetShowSuggestionsEvent>((_, ctx) => ({
        ...ctx
      })) as any
    }
  }
)
